import React from 'react';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  formatListingAge,
  formatListingDate,
  getListingAgeSourceDate,
  isSpiritListingType,
} from '../../util/listingRules';
import { getTimedSaleEndUnix } from '../../util/timedSales';
import css from './ListingPage.module.css';

import DescriptionTab from './DescriptionTab';
import BidHistoryTab from './BidHistoryTab';
import CaskTab from './CaskTab';
import {
  formatPublicMoney,
  formatUnixToDate,
  getEnumLabelFromListingFields,
  titleCaseFromSlug,
} from './helpers';

const hasDisplayValue = value => value != null && value !== '';

const formatSpiritTypeLabel = (listingFieldsConfig, rawValue) => {
  if (!rawValue) {
    return '';
  }

  const label = getEnumLabelFromListingFields(listingFieldsConfig, 'spirit_type', rawValue);
  return label === rawValue ? titleCaseFromSlug(label) : label;
};

const buildSpecificationRows = (publicData, intl) => {
  const auctionEndUnix = getTimedSaleEndUnix(publicData);
  const maybeMoneyRow = (id, value) => {
    const formattedValue = formatPublicMoney(value, intl);
    return formattedValue ? { id, value: formattedValue } : null;
  };

  return [
    hasDisplayValue(publicData?.currentAbv)
      ? { id: 'ListingPage.specs.currentAbv', value: `${publicData.currentAbv}%` }
      : null,
    hasDisplayValue(publicData?.barrel_volume)
      ? { id: 'ListingPage.specs.barrelVolume', value: publicData.barrel_volume }
      : null,
    hasDisplayValue(publicData?.entry_proof)
      ? { id: 'ListingPage.specs.entryProof', value: publicData.entry_proof }
      : null,
    hasDisplayValue(publicData?.lot_code)
      ? { id: 'ListingPage.specs.lotCode', value: publicData.lot_code }
      : null,
    hasDisplayValue(publicData?.currentLocation)
      ? { id: 'ListingPage.specs.warehouseLocation', value: publicData.currentLocation }
      : null,
    maybeMoneyRow('ListingPage.specs.buyNowPrice', publicData?.buyNowPrice),
    maybeMoneyRow('ListingPage.specs.reservePrice', publicData?.reservePrice),
    publicData?.auctionStartUnix
      ? {
          id: 'ListingPage.specs.auctionStart',
          value: formatUnixToDate(publicData.auctionStartUnix, intl),
        }
      : null,
    auctionEndUnix
      ? {
          id: 'ListingPage.specs.auctionEnd',
          value: formatUnixToDate(auctionEndUnix, intl),
        }
      : null,
  ].filter(row => row?.value);
};

const ContentTabs = ({
  activeTab,
  onTab,
  title,
  publicData,
  description,
  currentUser,
  bidCount,
  intl,
  listingFieldsConfig = [],
  bidsToRender = [],
  auctionEnabled = false,
}) => {
  const fallbackIntl = useIntl();
  const activeIntl = intl?.formatDate && intl?.formatNumber ? intl : fallbackIntl;
  const showBidHistory = !!auctionEnabled;
  const showCaskSpecifications = isSpiritListingType(publicData?.listingType);
  const activeTabToShow =
    activeTab === 'cask' && showCaskSpecifications
      ? 'cask'
      : activeTab === 'bids' && showBidHistory
      ? 'bids'
      : 'description';
  const spiritTypeLabel = formatSpiritTypeLabel(listingFieldsConfig, publicData?.spirit_type);

  return (
    <div className={css.contentTabsWrapper}>
      <div className={css.tabs}>
        <span
          className={activeTabToShow === 'description' ? css.active : ''}
          role="button"
          tabIndex={0}
          onClick={() => onTab('description')}
          onKeyDown={e => (e.key === 'Enter' ? onTab('description') : null)}
        >
          <FormattedMessage id="ListingPage.tabs.description" defaultMessage="Description" />
        </span>

        {showCaskSpecifications ? (
          <span
            className={activeTabToShow === 'cask' ? css.active : ''}
            role="button"
            tabIndex={0}
            onClick={() => onTab('cask')}
            onKeyDown={e => (e.key === 'Enter' ? onTab('cask') : null)}
          >
            <FormattedMessage
              id="ListingPage.tabs.caskSpecifications"
              defaultMessage="Cask Specifications"
            />
          </span>
        ) : null}

        {showBidHistory ? (
          <span
            className={activeTabToShow === 'bids' ? css.active : ''}
            role="button"
            tabIndex={0}
            onClick={() => onTab('bids')}
            onKeyDown={e => (e.key === 'Enter' ? onTab('bids') : null)}
          >
            <FormattedMessage
              id="ListingPage.tabs.bidHistory"
              defaultMessage="Bid History ({count})"
              values={{ count: bidCount }}
            />
          </span>
        ) : null}
      </div>

      <div className={css.tabContent}>
        {activeTabToShow === 'description' ? (
          <DescriptionTab
            title={title}
            longDescription={publicData?.longDescription}
            description={description}
          />
        ) : null}

        {showCaskSpecifications && activeTabToShow === 'cask' ? (
          <CaskTab
            producerLabel={titleCaseFromSlug(publicData?.producer)}
            filledDateLabel={formatListingDate(getListingAgeSourceDate(publicData), activeIntl)}
            spiritTypeLabel={spiritTypeLabel}
            listingAgeLabel={formatListingAge(publicData, activeIntl)}
            mashbill={publicData?.mashbill}
            auctionFieldRows={buildSpecificationRows(publicData, activeIntl)}
          />
        ) : null}

        {showBidHistory && activeTabToShow === 'bids' ? (
          <BidHistoryTab bidsToRender={bidsToRender} currentUser={currentUser} intl={activeIntl} />
        ) : null}
      </div>
    </div>
  );
};

export default ContentTabs;
